<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  buildBoardPayload,
  isBoardFormValid,
} from './pipelineBoardCreateUtils';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const dialogRef = ref(null);

const form = reactive({ name: '', template: '', stages: '' });

const templateOptions = computed(() => [
  { value: '', label: t('PIPELINE.CREATE_BOARD.TEMPLATE_NONE') },
  { value: 'sales', label: t('PIPELINE.CREATE_BOARD.TEMPLATE_SALES') },
  { value: 'support', label: t('PIPELINE.CREATE_BOARD.TEMPLATE_SUPPORT') },
]);

const isFormInvalid = computed(() => !isBoardFormValid(form));

const resetForm = () => {
  form.name = '';
  form.template = '';
  form.stages = '';
};

const open = () => dialogRef.value?.open();

const onSuccess = () => {
  resetForm();
  dialogRef.value?.close();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', buildBoardPayload(form));
};

defineExpose({ onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('PIPELINE.CREATE_BOARD.TITLE')"
    :confirm-button-label="t('PIPELINE.CREATE_BOARD.CONFIRM')"
    :disable-confirm-button="isFormInvalid"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :placeholder="t('PIPELINE.CREATE_BOARD.NAME_PLACEHOLDER')"
        :disabled="isLoading"
        autofocus
      />
      <Select
        v-model="form.template"
        class="!w-full"
        :options="templateOptions"
        :aria-label="t('PIPELINE.CREATE_BOARD.TEMPLATE_LABEL')"
        :disabled="isLoading"
      />
      <div v-if="!form.template" class="flex flex-col gap-1">
        <Input
          v-model="form.stages"
          :placeholder="t('PIPELINE.CREATE_BOARD.STAGES_PLACEHOLDER')"
          :disabled="isLoading"
        />
        <p class="text-xs text-n-slate-11">
          {{ t('PIPELINE.CREATE_BOARD.STAGES_HINT') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
